/**
 * Gallery Slider — Splide initialization
 *
 * Bundles the @splidejs/splide package and initializes any
 * `.corsa-gallery-slider__splide` containers on the page.
 *
 * The visual styling lives in `src/scss/blocks/_gallery-slider.scss`.
 * The base structural CSS that Splide requires is enqueued separately
 * from `assets/css/vendor/splide-core.min.css`.
 */

import Splide from '@splidejs/splide';

/**
 * Position the arrow container at the bottom-right of the active
 * slide photo. Sets both `right` and `bottom` so the arrows track the
 * photo's right + bottom edges regardless of any right-side peek on
 * the next slide or any caption-height variation.
 */
function positionArrows( splideEl ) {
	const arrows = splideEl.querySelector( '.corsa-gallery-slider__arrows' );
	const photo  = splideEl.querySelector( '.is-active .corsa-gallery-slider__slide-photo' )
		|| splideEl.querySelector( '.corsa-gallery-slider__slide-photo' );

	if ( ! arrows || ! photo ) {
		return;
	}

	const splideRect = splideEl.getBoundingClientRect();
	const photoRect  = photo.getBoundingClientRect();

	arrows.style.bottom = ( splideRect.bottom - photoRect.bottom ) + 'px';
	arrows.style.right  = ( splideRect.right  - photoRect.right  ) + 'px';
}

function initGallerySliders() {
	const sliders = document.querySelectorAll( '.corsa-gallery-slider__splide' );

	if ( ! sliders.length ) {
		return;
	}

	sliders.forEach( ( el ) => {
		const isLoop = el.hasAttribute( 'data-loop' );

		const splide = new Splide( el, {
			type: isLoop ? 'loop' : 'slide',
			perPage: 1,
			perMove: 1,
			gap: '30px',
			// Reserve right-side room inside the track so the next
			// slide peeks past the active one. Matches the Figma
			// bleed-off-right design.
			padding: { right: '22%' },
			arrows: true,
			pagination: false,
			drag: true,
			// On mobile there isn't room for a peek without pushing
			// the slide past the viewport edge, so collapse the
			// padding and the inter-slide gap.
			breakpoints: {
				767: {
					padding: 0,
					gap: 0,
				},
			},
		} );

		splide.on( 'mounted', () => positionArrows( el ) );
		splide.on( 'resized', () => positionArrows( el ) );

		splide.mount();
	} );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', initGallerySliders );
} else {
	initGallerySliders();
}
